import apiService from './apiService';
import { API_ENDPOINTS } from './apiEndpoints';

const FILE_KEY_TO_API_FIELD = {
  payment_confirmation: 'payment_confirmation_file',
  payment_screenshot: 'payment_screenshot',
  cheque_image: 'cheque_image',
};

const ALLOWED_FILE_API_FIELDS = new Set([
  'payment_screenshot',
  'payment_confirmation_file',
  'cheque_image',
]);

const TRANSACTION_ID_FIELD_KEYS = new Set([
  'transaction_reference_no',
  'transaction_id',
]);

const appendIfPresent = (formData, key, value) => {
  if (value == null) return;
  const str = String(value).trim();
  if (str !== '') {
    formData.append(key, str);
  }
};

export const extractPaymentApiError = (error) => {
  if (!error) return 'Failed to update payment status';
  if (typeof error === 'string') return error;
  if (typeof error.detail === 'string') return error.detail;
  if (Array.isArray(error.detail)) return error.detail.join(', ');
  if (error.message) return error.message;
  if (error.error) return String(error.error);
  return 'Failed to update payment status';
};

export const getAdvancePaymentEntries = (order = {}) => {
  const raw = order?.advance_payment;
  if (!raw) return [];
  if (Array.isArray(raw)) {
    return raw.filter((entry) => entry && !entry.is_deleted);
  }
  return [raw];
};

export const getOrderPaymentDetailsFromOrder = (order = {}) => {
  const totalAmount = Number(order?.total_amount ?? order?.total ?? 0);
  const entries = getAdvancePaymentEntries(order);
  const latestEntry = entries[entries.length - 1] || {};

  const paidFromEntries = entries.reduce(
    (sum, entry) => sum + Number(entry.advance_amount || 0),
    0
  );
  const paidAmount = Number(
    paidFromEntries ||
      latestEntry.advance_amount ||
      order?.amount_paid ||
      order?.paid ||
      0
  );
  const remainingAmount = Number(
    latestEntry.remaining_amount ??
      order?.remaining_amount ??
      Math.max(0, totalAmount - paidAmount)
  );

  return {
    order_id: order?.order_id,
    totalAmount,
    paidAmount,
    remainingAmount,
  };
};

export const isZeroRemainingPayment = (modalState) => {
  const remainingAmount = Number(modalState?.orderPaymentDetails?.remainingAmount ?? 0);
  return remainingAmount === 0;
};

export const getPaidAmountForRequest = (modalState) => {
  const orderDetails = modalState?.orderPaymentDetails;
  return Number(orderDetails?.remainingAmount ?? modalState?.advanceAmount ?? 0);
};

/**
 * Builds multipart FormData for new_update_payment_status (Paid only).
 * When remaining is 0, only mandatory fields are sent.
 */
export const buildNewPaidPaymentFormData = (modalState, orderId) => {
  const userId = localStorage.getItem('user_id');
  const paidAmount = getPaidAmountForRequest(modalState);
  const zeroRemaining = isZeroRemainingPayment(modalState);

  const formData = new FormData();
  formData.append('user_id', userId || '');
  formData.append('order_id', orderId);
  formData.append('payment_status', 'Paid');
  formData.append('paid_amount', String(paidAmount));

  if (zeroRemaining) {
    return formData;
  }

  const fields = modalState?.paymentFields || {};
  const files = modalState?.paymentFiles || {};
  const methodConfig = modalState?.paymentMethodConfig;

  const transactionId = [...TRANSACTION_ID_FIELD_KEYS]
    .map((key) => fields[key])
    .find((value) => value != null && String(value).trim() !== '');

  appendIfPresent(formData, 'transaction_id', transactionId);

  const comment =
    fields.comment ??
    fields.sender_bank_name ??
    (fields.cheque_number
      ? `Cheque #${fields.cheque_number}${fields.bank_name_branch ? ` - ${fields.bank_name_branch}` : ''}`
      : '');
  appendIfPresent(formData, 'comment', comment);

  if (methodConfig?.id) {
    formData.append('method', methodConfig.id);
  }

  appendIfPresent(formData, 'receiver_name', fields.receiver_name);

  Object.entries(fields).forEach(([key, value]) => {
    if (value == null || String(value).trim() === '') return;
    if (TRANSACTION_ID_FIELD_KEYS.has(key)) return;
    if (key === 'receiver_name' || key === 'comment' || key === 'sender_bank_name') return;
    appendIfPresent(formData, key, value);
  });

  Object.entries(files).forEach(([key, file]) => {
    if (!(file instanceof File)) return;
    const apiField = FILE_KEY_TO_API_FIELD[key] || key;
    if (ALLOWED_FILE_API_FIELDS.has(apiField)) {
      formData.append(apiField, file);
    }
  });

  return formData;
};

export const validateNewPaidPaymentRequest = (modalState) => {
  const orderDetails = modalState?.orderPaymentDetails;
  const remainingAmount = Number(orderDetails?.remainingAmount ?? 0);
  const paidAmount = getPaidAmountForRequest(modalState);

  if (remainingAmount === 0) {
    if (paidAmount !== 0) {
      return 'Paid amount must be 0 when nothing is remaining';
    }
    return '';
  }

  if (!paidAmount || paidAmount <= 0) {
    return 'Paid amount must be greater than 0';
  }
  if (paidAmount !== remainingAmount) {
    return 'Payment amount must equal the remaining amount';
  }
  if (!modalState?.paymentMethod) {
    return 'Please select a payment method';
  }
  if (!modalState?.paymentMethodConfig) {
    return 'Please select a valid payment method';
  }

  return '';
};

export const validatePaidPaymentFormDetails = (modalState, formConfig) => {
  if (isZeroRemainingPayment(modalState)) {
    return '';
  }

  if (!formConfig) {
    return 'Please select a payment method';
  }

  const orderDetails = modalState.orderPaymentDetails;
  const remainingAmount = Number(orderDetails?.remainingAmount ?? 0);
  const fields = modalState.paymentFields || {};

  const missingField = formConfig.fields?.find((field) => {
    if (!field.required) return false;
    const value = String(fields[field.key] || '').trim();
    if (field.key === 'cheque_amount' && orderDetails) {
      return !value && remainingAmount > 0;
    }
    return !value;
  });

  if (missingField) {
    return `Please enter ${missingField.label}`;
  }

  if (formConfig.type === 'bank_transfer') {
    const ref = fields.transaction_reference_no || fields.transaction_id || '';
    if (!/^[A-Z0-9]+$/.test(ref)) {
      return 'Transaction Reference No. must contain only English letters and numbers (A–Z, 0–9)';
    }
  }

  if (formConfig.type === 'cheque') {
    const chequeNo = fields.cheque_number || '';
    if (!/^\d{1,6}$/.test(chequeNo)) {
      return 'Cheque Number must be 1 to 6 digits';
    }
    const chequeAmount = Number(fields.cheque_amount || remainingAmount || 0);
    if (chequeAmount !== remainingAmount) {
      return 'Cheque amount must equal the remaining amount';
    }
    if (!fields.cheque_date) {
      return 'Please select Cheque Date';
    }
  }

  if (formConfig.type === 'cash') {
    const receiver = fields.receiver_name || '';
    if (!/^[a-zA-Z\s]+$/.test(receiver.trim())) {
      return 'Receiver Name must contain only English letters';
    }
  }

  if (
    formConfig.fileField?.required &&
    !(modalState.paymentFiles?.[formConfig.fileField.key] instanceof File)
  ) {
    return `Please upload ${formConfig.fileField.label}`;
  }

  return '';
};

/**
 * PUT multipart/form-data to new_update_payment_status (Paid only).
 */
export const submitNewPaidPaymentStatus = async (modalState, orderId) => {
  if (!orderId) {
    throw { success: false, message: 'Order ID is required' };
  }

  const validationError = validateNewPaidPaymentRequest(modalState);
  if (validationError) {
    throw { success: false, message: validationError };
  }

  const formDetailsError = validatePaidPaymentFormDetails(
    modalState,
    modalState.paymentMethodConfig
  );
  if (formDetailsError) {
    throw { success: false, message: formDetailsError };
  }

  const formData = buildNewPaidPaymentFormData(modalState, orderId);
  return apiService.putMultipart(API_ENDPOINTS.UPDATE_new_payment_status, formData);
};

export default {
  submitNewPaidPaymentStatus,
  buildNewPaidPaymentFormData,
  validateNewPaidPaymentRequest,
  validatePaidPaymentFormDetails,
  extractPaymentApiError,
  getOrderPaymentDetailsFromOrder,
  getAdvancePaymentEntries,
  isZeroRemainingPayment,
  getPaidAmountForRequest,
};
